import { useEffect, useState, useMemo } from "react";
import useResponsive from "@/hooks/useResponsive";
import apiClient from "../utils/apiClient";
import {
  Pencil,
  FileText,
  Mail,
  Search,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

import "./BookingsPage.css";

const STATUS_TABS = ["all", "draft", "initialized", "in_progress", "confirmed"];

export default function BookingsPage() {
const { isMobile } = useResponsive();

  const [allBookings, setAllBookings] = useState([]);

  const [bookings, setBookings] = useState([]);
  const [status, setStatus] = useState("all");

  const [loading, setLoading] = useState(false);

  const [voucherTrip, setVoucherTrip] = useState(null);

  const [selectedService, setSelectedService] = useState(null);

  const [hotelSearch, setHotelSearch] = useState("");

  const [hotelResults, setHotelResults] = useState([]);

  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchBookings();
  }, [status]);

  const fetchBookings = async () => {
    try {
      setLoading(true);

      const res = await apiClient.get("/bookings/advanced");

      const payload = res?.data?.data;

      const normalized = Array.isArray(payload) ? payload : [];

      setAllBookings(normalized);

      let filtered = normalized;

      if (status !== "all") {
        filtered = normalized.filter(
          (b) => (b.status || "initialized") === status,
        );
      }

      setBookings(filtered);
    } catch (err) {
      console.error("Bookings load failed:", err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hotelSearch?.trim()) {
      setHotelResults([]);

      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await apiClient.get(`/hotels?search=${hotelSearch}`);

        setHotelResults(res?.data?.data || []);
      } catch (err) {
        console.error("Hotel search failed", err);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [hotelSearch]);

const filteredBookings = useMemo(() => {

  const query = search.trim().toLowerCase();

  if (!query) {
    return bookings;
  }

  const scoreBooking = (b) => {

    let score = 0;

    const phone = String(b.leadId?.phone || "").toLowerCase();
    const leadId = String(b.leadId?.leadId || "").toLowerCase();
    const bookingRef = String(b.bookingReference || "").toLowerCase();
    const customer = String(
      b.leadId?.customerName ||
      b.customer?.name ||
      ""
    ).toLowerCase();
    const city = String(b.destination?.city || "").toLowerCase();
    const country = String(b.destination?.country || "").toLowerCase();

    if (phone === query) score += 1000;
    else if (phone.startsWith(query)) score += 900;
    else if (phone.includes(query)) score += 800;

    if (leadId === query) score += 700;
    else if (leadId.startsWith(query)) score += 650;
    else if (leadId.includes(query)) score += 600;

    if (bookingRef === query) score += 550;
    else if (bookingRef.startsWith(query)) score += 500;
    else if (bookingRef.includes(query)) score += 450;

    if (customer.startsWith(query)) score += 400;
    else if (customer.includes(query)) score += 350;

    if (city.startsWith(query)) score += 300;
    else if (city.includes(query)) score += 250;

    if (country.startsWith(query)) score += 200;
    else if (country.includes(query)) score += 150;

    return score;
  };

  return bookings
    .map((booking) => ({
      booking,
      score: scoreBooking(booking),
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ booking }) => booking);

}, [bookings, search]);

  return (
    <div className="layout">
      {/* SIDEBAR */}
      <div className="sidebar">
        <div className="sidebar-title">Bookings</div>

        {STATUS_TABS.map((s) => (
          <div
            key={s}
            className={status === s ? "active" : ""}
            onClick={() => setStatus(s)}
          >
            {
              {
                all: `All (${allBookings.length})`,

                draft: `Draft (${
                  allBookings.filter((b) => b.status === "draft").length
                })`,

                initialized: `Initialized (${
                  allBookings.filter(
                    (b) => (b.status || "initialized") === "initialized",
                  ).length
                })`,

                in_progress: `In Progress (${
                  allBookings.filter((b) => b.status === "in_progress").length
                })`,

                confirmed: `Confirmed (${
                  allBookings.filter((b) => b.status === "confirmed").length
                })`,
              }[s]
            }
          </div>
        ))}
      </div>

      {/* MAIN */}
      <div className="table">
        {/* TOP BAR */}
        <div className="table-top">
          <div className="top-left">
            <h2>Operations Bookings</h2>

            <span className="count">{filteredBookings.length} bookings</span>
          </div>

          <div
            className="search-box"
               style={{
             width: isMobile ? "100%" : undefined,
            }}
            >
            <Search size={14} />

            <input
              placeholder="Search bookings..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="row">
          <div className="cell">
            <b>Booking</b>
          </div>

          <div className="cell">
            <b>Hotel</b>
          </div>

          <div className="cell">
            <b>Duration</b>
          </div>

          <div className="cell">
            <b>Status</b>
          </div>

          <div className="cell">
            <b>Tag</b>
          </div>

          <div className="cell">
            <b>Comments</b>
          </div>
        </div>

        {/* LOADING */}
        {loading && <div className="loading">Loading bookings...</div>}

        {/* EMPTY */}
        {!loading && filteredBookings.length === 0 && (
          <div className="empty">No bookings found</div>
        )}

        {/* DATA */}
        {!loading &&
          filteredBookings.map((booking) => {
            const bookingId = booking._id;

            const services = Array.isArray(booking.services)
              ? booking.services
              : [];

            return (
              <div key={bookingId} className="booking-group">
                {/* MASTER ROW */}
                <div className="row master-row">
                  {/* BOOKING */}
                  <div className="cell">
                    <div className="trip">
                      <div className="name">
                        {booking.customerName || "Customer"}
                        {" • "}
                        {booking.destinationName || "-"}

                        {booking.isOutOfSync && (
                          <AlertTriangle size={14} color="orange" />
                        )}

                        {booking.status === "confirmed" && (
                          <CheckCircle2 size={14} color="green" />
                        )}
                      </div>

                      <div className="meta">
                        Lead ID: {booking.leadReference || "-"}
                      </div>

                      <div className="meta">
                        Phone: {booking.customerPhone || "-"}
                      </div>

                      <div className="meta">
                        Departure:{" "}
                        {booking.departureDate
                          ? new Date(booking.departureDate).toLocaleDateString()
                          : "-"}
                      </div>

                      <div className="meta">
                        Sales: {booking.salesOwner || "Admin"}
                      </div>

                      <div className="meta">
                        Ops: {booking.opsOwner || "Admin"}
                      </div>

                      <div className="meta">Hotels: {services.length}</div>
                    </div>
                  </div>

                  {/* HOTEL */}
                  <div className="cell hotel">
                    {services.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <strong
                            style={{
                              display: "block",
                              maxWidth: "170px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {s.name}
                          </strong>

                          <Pencil
                            size={14}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setSelectedService(s);

                              setHotelSearch(s.name || "");
                            }}
                          />

                          <Mail
                            size={14}
                            style={{ cursor: "pointer" }}
                            onClick={async () => {
                              try {
                                const res = await apiClient.get(
                                  `/services/${s.id}/reservation-text`,
                                );

                                await navigator.clipboard.writeText(
                                  res.data.text,
                                );

                                alert("Reservation copied");
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                          />

                          {(s.status === "confirmed" ||
                            s.status === "booked") && (
                            <FileText
                              size={14}
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => setVoucherTrip(booking)}
                            />
                          )}
                        </div>

<div className="sub">
  {s.mealPlan}
  {" • "}
  {s.adults}A
</div>

<div
  style={{
    fontSize: "11px",
    color: "#64748b",
    marginTop: "2px",
  }}
>
  {s.checkIn
    ? new Date(s.checkIn).toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
        }
      )
    : "-"}

  {" → "}

  {s.checkOut
    ? new Date(s.checkOut).toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
        }
      )
    : "-"}
</div>

                      </div>
                    ))}
                  </div>

                  {/* DURATION */}
                  <div className="cell">
                    {services.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          marginBottom: "12px",
                        }}
                      >
                        <>
  {s.checkIn
    ? new Date(s.checkIn).toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
        }
      )
    : "-"}

  {" • "}

  {s.nights || 0}N
</>
                      </div>
                    ))}
                  </div>

                  {/* STATUS */}

                  <div className="cell">
                    {services.map((s) => (
                      <select
                        key={s.id}
                        disabled={s.status === "changed"}
                        value={s.status || "initialized"}
                        style={{
                          marginBottom: "12px",
                          opacity: s.status === "changed" ? 0.6 : 1,
                          cursor:
                            s.status === "changed" ? "not-allowed" : "pointer",
                        }}
                        onChange={async (e) => {
                          await apiClient.put(`/services/${s.id}/status`, {
                            status: e.target.value,
                          });

                          fetchBookings();
                        }}
                      >

{s.status === "initialized" && (
  <>
    <option value="initialized">
      Initialized
    </option>

    <option value="in_progress">
      In Progress
    </option>

    <option value="confirmed">
      Confirmed
    </option>
  </>
)}

{s.status === "in_progress" && (
  <>
    <option value="in_progress">
      In Progress
    </option>

    <option value="confirmed">
      Confirmed
    </option>
  </>
)}

{s.status === "confirmed" && (
  <option value="confirmed">
    Confirmed
  </option>
)}

{s.status === "changed" && (
  <option value="changed">
    Changed
  </option>
)}

                      </select>
                    ))}
                  </div>

                  {/* TAG */}
                  <div className="cell">-</div>

                  {/* COMMENTS */}
                  <div className="cell">-</div>
                </div>
              </div>
            );
          })}

        {selectedService && (
          <div className="voucher-overlay">
            <div className="voucher-modal">
              <div className="voucher-header">
                <h3>Edit Hotel</h3>

                <button
                  className="voucher-close"
                  onClick={() => setSelectedService(null)}
                >
                  ×
                </button>
              </div>

              <div className="voucher-body">
                <input
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    marginBottom: "12px",
                  }}
                  value={hotelSearch}
                  onChange={(e) => setHotelSearch(e.target.value)}
                  placeholder="Search Hotel..."
                />

                <div className="price-field">
                  <label>Hotel Name</label>

                  <input
                    style={{
                      width: "100%",
                      padding: "10px",
                    }}
                    value={selectedService?.name || ""}
                    onChange={(e) =>
                      setSelectedService({
                        ...selectedService,
                        name: e.target.value,
                      })
                    }
                  />
                </div>


                <div className="price-field">
                  <label>Base Cost</label>

                  <input
                    type="number"
                    value={selectedService?.baseCost || 0}
                    onChange={(e) =>
                      setSelectedService({
                        ...selectedService,
                        baseCost: Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="price-field">
                  <label>Extra Adult Price</label>

                  <input
                    type="number"
                    value={selectedService?.extraAdultPrice || 0}
                    onChange={(e) =>
                      setSelectedService({
                        ...selectedService,
                        extraAdultPrice: Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="price-field">
                  <label>Child With Bed (CWB)</label>

                  <input
                    type="number"
                    value={selectedService?.cwbPrice || 0}
                    onChange={(e) =>
                      setSelectedService({
                        ...selectedService,
                        cwbPrice: Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="price-field">
                  <label>Child No Bed (CNB)</label>

                  <input
                    type="number"
                    value={selectedService?.cnbPrice || 0}
                    onChange={(e) =>
                      setSelectedService({
                        ...selectedService,
                        cnbPrice: Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div
                  style={{
                    marginTop: "15px",
                    maxHeight: "220px",
                    overflow: "auto",
                    border: "1px solid #eee",
                  }}
                >
                  {hotelResults.map((hotel) => (
                    <div
                      key={hotel._id}
                      style={{
                        padding: "10px",
                        borderBottom: "1px solid #eee",
                        cursor: "pointer",
                      }}
                      onClick={async () => {
                        try {
                          const hotelResponse = await apiClient.get(
                            `/hotels/${hotel._id}`,
                          );

                          const selectedHotel = hotelResponse.data?.data;

                          const firstRate =
                            selectedHotel?.seasonalRates?.[0]?.roomTypes?.[0]
                              ?.mealPlans?.[0]?.rates || {};

                          setSelectedService({
                            ...selectedService,

                            hotelId: selectedHotel._id,
                            hotelName: selectedHotel.name,
                            name: selectedHotel.name,

                            baseCost: firstRate.baseRate || 0,

                            extraAdultPrice: firstRate.extraAdultRate || 0,

                            cwbPrice: firstRate.childWithBedRate || 0,

                            cnbPrice: firstRate.childNoBedRate || 0,
                          });
                        } catch (err) {
                          console.error("Hotel load failed", err);
                        }
                      }}
                    >
                      <strong
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "220px",
                        }}
                      >
                        {hotel.name}
                      </strong>

                      <br />

                      <small>{hotel?.location?.city}</small>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: "15px",
                  }}
                >
                  <button
                    className="voucher-download-btn"
                    onClick={async () => {
                      try {
                        await apiClient.put(`/services/${selectedService.id}`, {
                          hotelId: selectedService.hotelId,

                          hotelName: selectedService.name,


                          status: "changed",

                          pricing: {
                            baseCost: Number(selectedService.baseCost || 0),

                            extraAdultPrice: Number(
                              selectedService.extraAdultPrice || 0,
                            ),

                            cwbPrice: Number(selectedService.cwbPrice || 0),

                            cnbPrice: Number(selectedService.cnbPrice || 0),

                            markup: Number(selectedService.markup || 0),

                            finalPrice:
                              Number(selectedService.baseCost || 0) +
                              Number(selectedService.markup || 0),
                          },
                        });

                        setSelectedService(null);

                        fetchBookings();
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* =====================================================
    VOUCHER MODAL
===================================================== */}

        {voucherTrip && (
          <div className="voucher-overlay">
            <div className="voucher-modal">
              <div className="voucher-header">
                <h3>Hotel Voucher</h3>

                <button
                  className="voucher-close"
                  onClick={() => setVoucherTrip(null)}
                >
                  ×
                </button>
              </div>

              <div className="voucher-body">
                <div className="voucher-customer">
                  {voucherTrip.customerName}
                </div>

                <div className="voucher-booking-ref">
                  {voucherTrip.bookingReference}
                </div>
              </div>

              <div className="voucher-actions">
                <button
                  className="voucher-download-btn"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("token");

                      const response = await fetch(
                        `${import.meta.env.VITE_API_BASE_URL}/bookings/${voucherTrip._id}/voucher`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        },
                      );

                      if (!response.ok) {
                        throw new Error("Failed to download voucher");
                      }

                      const blob = await response.blob();

                      const url = window.URL.createObjectURL(blob);

                      const a = document.createElement("a");

                      a.href = url;

                      a.download = `voucher-${voucherTrip.bookingReference}.pdf`;

                      document.body.appendChild(a);

                      a.click();

                      a.remove();

                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error(error);

                      alert("Voucher download failed");
                    }
                  }}
                >
                  Download Voucher
                </button>

                <button
                  className="voucher-cancel-btn"
                  onClick={() => setVoucherTrip(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
